import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const companyId = searchParams.get('cid');
  const employeeId = searchParams.get('eid');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token || !companyId || !employeeId) {
      setError('Invalid invite link. Please check the URL.');
    }
  }, [token, companyId, employeeId]);

  const [mode, setMode] = useState('create'); // 'create' or 'link'

  async function handleSubmit(e) {
    e.preventDefault();

    if (mode === 'create' && password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (!token || !companyId || !employeeId) {
        return setError('Invalid invite parameters.');
    }

    try {
      setError('');
      setLoading(true);

      // 1. If 'create', call backend to create user + link
      // 2. If 'link', we first login via Firebase client, THEN call backend to link
      
      if (mode === 'link') {
        // Login first to verify credentials
        try {
            await login(email, password);
        } catch (authErr) {
            throw new Error('Invalid email or password.');
        }
      }

      // Call backend to accept invite (and create user if mode='create')
      const backendUrl = process.env.REACT_APP_BACKEND_URL_PROD || "http://localhost:8000";
      const response = await fetch(`${backendUrl}/api/auth/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          email,
          password: mode === 'create' ? password : null, // Don't send password if linking
          companyId,
          employeeId
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invite');
      }

      // If 'create', login the user now
      if (mode === 'create') {
        await login(email, password);
      }
      
      navigate('/');
    } catch (err) {
      console.error(err);
      setError('Failed to process: ' + err.message);
    }
    setLoading(false);
  }

  if (!token || !companyId || !employeeId) {
      return (
        <div style={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            background: '#031128',
            padding: '20px',
            color: 'white',
            fontFamily: 'Montserrat'
          }}>
            <div style={{ textAlign: 'center' }}>
                <h2>Invalid Invite Link</h2>
                <p>Please contact your administrator.</p>
            </div>
        </div>
      )
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      background: '#031128',
      padding: '20px',
      fontFamily: 'Montserrat'
    }}>
      <div style={{ 
          maxWidth: '400px', 
          width: '100%', 
          padding: '40px',
          background: 'rgba(86, 141, 237, 0.08)',
          border: '1px solid rgba(86, 141, 237, 0.15)',
          borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <i className="fa-solid fa-user-plus" style={{ color: '#568ded', fontSize: '32px', marginBottom: '10px' }}></i>
          <h2 style={{ fontSize: '24px', fontWeight: 700, color: 'white' }}>Accept Invite</h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>Set up your account to access the Employee Portal</p>
        </div>
        
        {error && <div style={{ 
          background: 'rgba(235, 64, 64, 0.1)', 
          color: '#EB4040', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px'
        }}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
            <button 
                type="button"
                onClick={() => setMode('create')}
                style={{
                    padding: '8px 16px',
                    background: mode === 'create' ? '#568ded' : 'transparent',
                    border: '1px solid #568ded',
                    color: 'white',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '14px'
                }}
            >
                Create Account
            </button>
            <button 
                type="button"
                onClick={() => setMode('link')}
                style={{
                    padding: '8px 16px',
                    background: mode === 'link' ? '#568ded' : 'transparent',
                    border: '1px solid #568ded',
                    color: 'white',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: '14px'
                }}
            >
                Log In & Link
            </button>
        </div>

        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            {mode === 'create' ? (
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0 }}>
                    Use this if you <strong>don't</strong> have a JOBSEEQR account yet.
                </p>
            ) : (
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', margin: 0 }}>
                    Use this if you <strong>already</strong> have a JOBSEEQR account (e.g. from a previous job or application).
                </p>
            )}
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              style={{ 
                width: '100%', 
                padding: '12px', 
                borderRadius: '8px', 
                border: '1px solid rgba(86, 141, 237, 0.3)',
                background: 'rgba(255,255,255,0.05)',
                color: 'white',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>{mode === 'create' ? 'Create Password' : 'Password'}</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={mode === 'create' ? "Min. 6 characters" : "Enter your password"}
              minLength={mode === 'create' ? 6 : undefined}
              style={{ 
                width: '100%', 
                padding: '12px', 
                borderRadius: '8px', 
                border: '1px solid rgba(86, 141, 237, 0.3)',
                background: 'rgba(255,255,255,0.05)',
                color: 'white',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {mode === 'create' && (
            <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>Confirm Password</label>
                <input 
                type="password" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter password"
                minLength={6}
                style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(86, 141, 237, 0.3)',
                    background: 'rgba(255,255,255,0.05)',
                    color: 'white',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                }}
                />
            </div>
          )}

          <button disabled={loading} type="submit" style={{ 
              marginTop: '10px',
              padding: '12px',
              backgroundColor: '#568ded',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.2s'
          }}>
            {loading ? 'Processing...' : (mode === 'create' ? 'Create Account & Login' : 'Link Account & Login')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite;

